<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { request } from '@/utils/request'
import { useUIStore } from '@/stores/ui'
import { getFullUploadUrl } from '@/utils'

/**
 * @description 全局搜索弹窗（Ctrl / Cmd + K 唤起）
 *  - 输入关键词防抖搜索提示词案例
 *  - ↑↓ 选择，Enter 打开，Esc 关闭
 *  - 命中后直接跳转 /case/:id 独立详情页
 */
const router = useRouter()
const uiStore = useUIStore()

const keyword = ref('')
const results = ref<PublicCaseListItem[]>([])
const loading = ref(false)
const activeIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(keyword, (value) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  activeIndex.value = 0
  if (!value.trim()) {
    results.value = []
    loading.value = false
    return
  }
  loading.value = true
  debounceTimer = setTimeout(async () => {
    try {
      const res = await request<Pagination<PublicCaseListItem>>({
        url: '/prompt/cases',
        method: 'GET',
        params: { keyword: value.trim(), pageSize: 10 },
      })
      results.value = res.items
    } catch {
      results.value = []
    } finally {
      loading.value = false
    }
  }, 300)
})

function open() {
  uiStore.openSearch()
  nextTick(() => inputRef.value?.focus())
}

function close() {
  uiStore.closeSearch()
  keyword.value = ''
  results.value = []
  activeIndex.value = 0
}

function select(item: PublicCaseListItem) {
  router.push(`/case/${item.id}`)
}

function onKeydown(e: KeyboardEvent) {
  // Ctrl/Cmd + K 唤起
  if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    if (uiStore.searchOpen) close()
    else open()
    return
  }
  if (!uiStore.searchOpen) return

  if (e.key === 'Escape') {
    e.preventDefault()
    close()
  } else if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (results.value.length) activeIndex.value = (activeIndex.value + 1) % results.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (results.value.length) activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const item = results.value[activeIndex.value]
    if (item) select(item)
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

function onBackdropClick(e: MouseEvent) {
  if ((e.target as HTMLElement)?.dataset?.backdrop === 'true') close()
}
</script>

<template>
  <Teleport to="body">
    <div v-if="uiStore.searchOpen" class="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[15vh]"
      data-backdrop="true" @click="onBackdropClick">
      <!-- 遮罩 -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" data-backdrop="true" />

      <!-- 搜索面板 -->
      <div class="relative z-10 w-full max-w-xl animate-fade-in overflow-hidden rounded-2xl glass shadow-2xl">
        <!-- 输入区 -->
        <div class="flex items-center gap-3 border-b border-white/5 px-4 py-3">
          <span class="text-slate-500">🔍</span>
          <input ref="inputRef" v-model="keyword" type="text" placeholder="搜索提示词、标题…"
            class="flex-1 bg-transparent text-base text-slate-100 placeholder-slate-500 focus:outline-none" />
          <kbd class="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs text-slate-500">Esc</kbd>
        </div>

        <!-- 结果区 -->
        <div class="max-h-[50vh] overflow-y-auto">
          <div v-if="loading" class="grid place-items-center py-8 text-sm text-slate-500">搜索中…</div>
          <div v-else-if="keyword.trim() && !results.length"
            class="grid place-items-center py-8 text-sm text-slate-500">
            未找到匹配的案例
          </div>
          <div v-else-if="!keyword.trim()" class="px-4 py-6 text-center text-sm text-slate-500">
            输入关键词搜索 517 个提示词案例
          </div>
          <ul v-else class="py-2">
            <li v-for="(item, idx) in results" :key="item.id">
              <button class="flex w-full items-center gap-3 px-4 py-2.5 text-left transition"
                :class="idx === activeIndex ? 'bg-brand-500/10' : 'hover:bg-white/5'" @mouseenter="activeIndex = idx"
                @click="select(item)">
                <img :src="getFullUploadUrl(item.image)" :alt="item.title"
                  class="h-12 w-12 shrink-0 rounded-lg object-cover" loading="lazy" referrerpolicy="no-referrer" />
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-slate-100">{{ item.title }}</p>
                  <p class="truncate text-xs text-slate-500">
                    {{ item.category?.name || '未分类' }} · ❤ {{ item.likeCount }} · 👁 {{ item.viewCount }}
                  </p>
                </div>
                <span class="text-xs text-slate-600">↵</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </Teleport>
</template>